/**
 * @copyright Copyright (c) Open-Xchange GmbH, Germany <info@open-xchange.com>
 * @license AGPL-3.0
 *
 * This code is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with OX App Suite. If not, see <https://www.gnu.org/licenses/agpl-3.0.txt>.
 *
 * Any use of the work other than as authorized under this license or copyright law is prohibited.
 */

import ox from '@/ox'
import Backbone from '@/backbone'
import ModalDialog from '@/io.ox/backbone/views/modal'
import calendarModel from '@/io.ox/calendar/model'
import calendarApi from '@/io.ox/calendar/api'
import { createUpdateData, getCurrentRangeOptions, isAllday, updateRecurrenceDate } from '@/io.ox/calendar/util'
import yell from '@/io.ox/core/yell'
import gt from 'gettext'

export async function updateAppointment (model, updates = {}) {
  return new Promise(resolve => {
    const prevStartDate = model.getMoment('startDate')
    const prevEndDate = model.getMoment('endDate')
    const prevFolder = model.get('folder')
    const hasChanges = Object.entries(updates).reduce((memo, [key, value]) => {
      const isEqual = JSON.stringify(model.get(key)) === JSON.stringify(value)
      return memo || !isEqual
    }, false)
    if (!hasChanges) return resolve('no-changes')

    model.set(updates)

    function apiUpdate (model, options) {
      const { id, folder, recurrenceId, seriesId, rrule, startDate, endDate, timestamp } = model.toJSON()
      calendarApi.update({ id, folder, recurrenceId, seriesId, rrule, startDate, endDate, timestamp }, options).then(
        function success (data) {
          if (!data || !data.conflicts) return resolve('update')
          ox.load(() => import('@/io.ox/calendar/conflicts/conflictList')).then(function ({ default: conflictView }) {
            conflictView.dialog(data.conflicts)
              .on('cancel', reset)
              .on('ignore', function () {
                apiUpdate(model, Object.assign(options || {}, { checkConflicts: false }))
              })
          })
        },
        function fail (error) {
          yell(error)
          reset()
        }
      )
    }

    function reset () {
      model.set({
        startDate: model.previous('startDate'),
        endDate: model.previous('endDate'),
        folder: prevFolder
      })
      resolve('reset')
    }

    showRecurrenceDialog(model).then(({ action, rootModel }) => {
      switch (action) {
        case 'series': {
          // calculate new dates if old dates are available
          const oldStartDate = rootModel.getMoment('startDate')
          if (model.hasFlag('overridden')) {
            rootModel.set({
              startDate: model.get('startDate'),
              endDate: model.get('endDate')
            })
          } else {
            const startDate = rootModel.getMoment('startDate').add(model.getMoment('startDate').diff(prevStartDate, 'ms'), 'ms')
            const endDate = rootModel.getMoment('endDate').add(model.getMoment('endDate').diff(prevEndDate, 'ms'), 'ms')
            const format = isAllday(model) ? 'YYYYMMDD' : 'YYYYMMDD[T]HHmmss'
            rootModel.set({
              startDate: { value: startDate.format(format), tzid: rootModel.get('startDate').tzid },
              endDate: { value: endDate.format(format), tzid: rootModel.get('endDate').tzid }
            })
          }

          updateRecurrenceDate(rootModel, oldStartDate)
          apiUpdate(rootModel, Object.assign(getCurrentRangeOptions(), {
            checkConflicts: true
          }))
          break
        }
        case 'thisandfuture': {
          // calculate new dates if old dates are available use temporary new model to store data before the series split
          const updateModel = new calendarModel.Model(createUpdateData(rootModel, model))

          updateModel.set({
            startDate: model.get('startDate'),
            endDate: model.get('endDate')
          })

          // only if there is a new rrule set (if this and future is called on an exception we don't want to use the rrule from the root)
          if (model.get('rrule')) {
            updateModel.set('rrule', model.get('rrule'))
          }

          updateRecurrenceDate(updateModel, prevStartDate)
          apiUpdate(updateModel, {
            ...getCurrentRangeOptions(),
            checkConflicts: true,
            recurrenceRange: 'THISANDFUTURE'
          })
          break
        }
        case 'appointment':
          apiUpdate(model, { ...getCurrentRangeOptions(), checkConflicts: true })
          break
        default:
          reset()
      }
    })
  })
}

export const showRecurrenceDialog = async function (model, options) {
  const calendarAPI = (await import('@/io.ox/calendar/api')).default
  if (!(model instanceof Backbone.Model)) model = new calendarModel.Model(model)

  if (!model.get('recurrenceId')) return { action: 'appointment' }

  let rootModel
  try {
    rootModel = await calendarAPI.get({ id: model.get('seriesId'), folder: model.get('folder') }, false)
  } catch (e) {
    // in some rare cases we have series exceptions without a series root (don't ask me how someone achieves this). We only edit this single appointment then
    return { action: 'appointment' }
  }

  options = options || {}
  let text
  const teaser = gt('This appointment is part of a series.')
  const dialog = new ModalDialog({
    width: 620
  }).addCancelButton({ left: true })
  if (!options.dontAllowExceptions) dialog.addButton({ label: gt('Edit this appointment'), action: 'appointment', className: 'btn-default' })

  if (model.hasFlag('first_occurrence')) {
    if (options.dontAllowExceptions) return { action: 'series', rootModel }
    text = gt('Do you want to edit the whole series or just this appointment within the series?')
    dialog.addButton({ label: gt('Edit series'), action: 'series' })
  } else if (model.hasFlag('last_occurrence') && !options.allowEditOnLastOccurrence) {
    return { action: 'appointment' }
  } else if (options.dontAllowExceptions) {
    text = gt('Do you want to edit this and all future appointments or the whole series?')
    dialog.addButton({ label: gt('Edit series'), action: 'series', className: 'btn-default' })
    dialog.addButton({ label: gt('Edit all future appointments'), action: 'thisandfuture' })
  } else {
    text = gt('Do you want to edit this and all future appointments or just this appointment within the series?')
    dialog.addButton({ label: gt('Edit all future appointments'), action: 'thisandfuture' })
  }
  dialog.build(function () {
    this.$title.text(gt('Edit appointment'))
    this.$body.append(teaser, '\u00a0', text)
  }).open()

  const value = await new Promise(resolve => dialog.on('action', resolve))
  return { action: value, rootModel }
}
